import { useState } from "react";
import { toast } from "sonner";
import { Check, CreditCard, Landmark } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";

const AMOUNTS = [25, 50, 100, 250];

const BankDetails = () => (
  <div data-testid="bank-details" className="mt-6 rounded-2xl border border-ink/15 p-5 text-sm">
    {[["Account name", "[Account name]"], ["BSB", "[000-000]"], ["Account number", "[00000000]"], ["Reference", "Your name + DONATION"]].map(([k, v]) => (
      <p key={k} className="flex justify-between gap-4 border-b border-ink/10 py-2 last:border-0"><span className="text-ink/70">{k}</span><span className="font-mono">{v}</span></p>
    ))}
  </div>
);

export default function Donate() {
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [frequency, setFrequency] = useState("once");
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [method, setMethod] = useState("card");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const value = custom ? Number(custom) : amount;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (method === "card") {
        const { data } = await api.post("/payments/checkout", { ...form, amount: value, frequency, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
        return;
      }
      await api.post("/pledges", { ...form, amount: value, frequency });
      setDone(true);
      toast.success("Thank you — your pledge has been recorded.");
    } catch (err) { toast.error(formatError(err)); setBusy(false); return; }
    setBusy(false);
  };

  return (
    <main>
      <PageHero eyebrow="Donate" lines={["Give to", <span key="g" className="italic text-ochre">community.</span>]}
        intro="Give a one-off or monthly gift securely by card, or by direct bank transfer." image="/img/headland.jpg" />
      <section className="bg-sand px-5 py-20 sm:px-10">
        <div className="mx-auto grid max-w-[1440px] gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow text-ochre">Where it goes</p>
            <ul className="mt-6 space-y-6">
              {["Training programmes and course materials", "Mentoring and youth leadership", "Cultural and caring-for-Country activities"].map((t) => (
                <li key={t} className="flex gap-4 border-t border-ink/15 pt-6 font-display text-2xl"><Check className="mt-1 h-5 w-5 shrink-0 text-ochre" />{t}</li>
              ))}
            </ul>
            <p className="mt-10 text-sm text-ink/70">Tax-deductibility (DGR status): [confirm and add details here].</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-7">
            {done ? (
              <div data-testid="pledge-success" className="rounded-3xl bg-gum p-10 text-sand">
                <Check className="h-10 w-10" />
                <p className="mt-6 font-display text-4xl">Thank you, {form.name.split(" ")[0]}.</p>
                <p className="mt-3 text-sand/85">Your ${value} {frequency === "monthly" ? "monthly " : ""}pledge is recorded. We'll be in touch at {form.email}.</p>
                <div className="text-ink [&>div]:border-sand/30 [&>div]:bg-sand"><BankDetails /></div>
                <button data-testid="pledge-again-button" onClick={() => { setDone(false); setForm({ name: "", email: "", message: "" }); }} className="mt-8 text-sm underline underline-offset-4">Make another pledge</button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="pledge-form" className="rounded-3xl bg-white/60 p-8 sm:p-10">
                <div className="flex rounded-full bg-ink/5 p-1">
                  {["once", "monthly"].map((f) => (
                    <button type="button" key={f} data-testid={`frequency-${f}`} onClick={() => setFrequency(f)}
                      className={`flex-1 rounded-full py-3 text-sm font-semibold transition-colors ${frequency === f ? "bg-ink text-sand" : "text-ink/70"}`}>
                      {f === "once" ? "One-off" : "Monthly"}
                    </button>
                  ))}
                </div>
                <div className="mt-8 grid grid-cols-4 gap-3">
                  {AMOUNTS.map((a) => (
                    <button type="button" key={a} data-testid={`amount-${a}`} onClick={() => { setAmount(a); setCustom(""); }}
                      className={`rounded-2xl border py-5 font-display text-2xl transition-all duration-300 ${!custom && amount === a ? "border-ochre bg-ochre text-sand" : "border-ink/20 hover:border-ink"}`}>
                      ${a}
                    </button>
                  ))}
                </div>
                <input data-testid="amount-custom-input" type="number" min="1" placeholder="Other amount ($)" value={custom} onChange={(e) => setCustom(e.target.value)} className="field mt-6" />
                <input data-testid="pledge-name-input" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field mt-4" />
                <input data-testid="pledge-email-input" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="field mt-4" />
                <textarea data-testid="pledge-message-input" rows={2} placeholder="Message (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="field mt-4 resize-none" />
                <p className="eyebrow mt-8 text-ink/70">Pay by</p>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  {[["card", "Card", CreditCard], ["bank", "Bank transfer", Landmark]].map(([k, l, Icon]) => (
                    <button type="button" key={k} data-testid={`method-${k}`} onClick={() => setMethod(k)}
                      className={`flex items-center justify-center gap-2 rounded-2xl border py-4 text-sm font-semibold transition-colors ${method === k ? "border-ink bg-ink text-sand" : "border-ink/20 hover:border-ink"}`}>
                      <Icon className="h-4 w-4" /> {l}
                    </button>
                  ))}
                </div>
                {method === "bank" && <BankDetails />}
                <button data-testid="pledge-submit-button" disabled={busy || !value || value < 2} className="btn-ochre mt-8 w-full justify-center disabled:opacity-60">
                  {busy ? (method === "card" ? "Opening secure checkout…" : "Saving…") : `${method === "card" ? "Donate" : "Pledge"} $${value || 0}${frequency === "monthly" ? " / month" : ""}`}
                </button>
                <p className="mt-4 text-center text-xs text-ink/65">{method === "card" ? "Secure payment by Stripe. Card details never touch our site." : "We'll record your pledge — please transfer using the details above."}</p>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}
